import { useMemo, useState } from 'react';
import { Search, MapPin, Pencil, Trash2, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { useDeleteActivityFromLibrary } from '@/lib/library/hooks/useActivityLibrary';
import { LibraryActivity } from '@/lib/library/models/library-activity.model';
import { ActivityType } from '@/lib/builder/models/activity.model';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface LibraryActivitiesTableProps {
  /**
   * Tipi di attività su cui filtrare la libreria (accommodation, activity, flight, ...).
   * Array vuoto = nessun filtro, mostra tutti i tipi (coerente col filtro icone
   * multi-select in LibraryPage).
   */
  selectedActivityTypes: ActivityType[];
  /**
   * Attività di libreria già fetchate a livello di pagina (`LibraryPage`, una sola
   * chiamata `useActivityLibraryQuery()` senza filtri, eventualmente già ristrette
   * per owner scope se l'utente ha un'agenzia). Questo componente si occupa solo del
   * filtro client-side per tipo attività, tag, ricerca testuale e (per l'admin) ownerScope.
   */
  activities: LibraryActivity[];
  isLoading: boolean;
}

type OwnerScopeFilter = 'all' | 'user' | 'agency';

/**
 * Tabella "Libreria": filtra client-side le attività già fetchate dal genitore
 * (nessuna chiamata di rete qui), con filtro per tipo attività (multi-select,
 * gestito dal filtro a icone in LibraryPage), ricerca testuale, filtro per tag
 * e azioni di modifica/eliminazione riga.
 */
export function LibraryActivitiesTable({ selectedActivityTypes, activities, isLoading }: LibraryActivitiesTableProps) {
  const { t } = useI18n();
  const { user, userRole } = useAuth();
  const isAdmin = userRole === 'admin';

  const [search, setSearch] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [ownerScope, setOwnerScope] = useState<OwnerScopeFilter>('all');
  const [deleteTarget, setDeleteTarget] = useState<LibraryActivity | null>(null);

  const deleteMutation = useDeleteActivityFromLibrary();

  const activitiesForType = useMemo(
    () =>
      selectedActivityTypes.length === 0
        ? activities
        : activities.filter((activity) => selectedActivityTypes.includes(activity.activity_type)),
    [activities, selectedActivityTypes]
  );

  // Replica client-side lo stesso match testuale della RPC get_activity_library
  // (ILIKE case-insensitive su title/description/address), ora eseguito in memoria
  // sul dataset già fetchato invece che con una chiamata server per ogni carattere digitato.
  // Nota: `location` NON è un indirizzo/luogo in questo modello — è l'indice d'ordine
  // dell'attività nel giorno (vedi itinerary-builder.model.ts `addActivity`), quindi
  // il campo mostrato/cercato come "luogo" è `address`, non `location`.
  const searchedActivities = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    if (!searchLower) return activitiesForType;
    return activitiesForType.filter(
      (activity) =>
        activity.title.toLowerCase().includes(searchLower) ||
        (activity.description ?? '').toLowerCase().includes(searchLower) ||
        (activity.address ?? '').toLowerCase().includes(searchLower)
    );
  }, [activitiesForType, search]);

  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    searchedActivities.forEach((activity) => activity.tags.forEach((tag) => tagSet.add(tag)));
    return Array.from(tagSet).sort();
  }, [searchedActivities]);

  const filteredActivities = useMemo(() => {
    return searchedActivities.filter((activity) => {
      if (selectedTag && !activity.tags.includes(selectedTag)) return false;
      // Filtro extra disponibile solo per gli admin: la RLS SELECT già espone loro
      // tutte le righe (utente + agenzia), qui si aggiunge solo un narrowing lato UI.
      if (isAdmin && ownerScope !== 'all' && activity.owner_type !== ownerScope) return false;
      return true;
    });
  }, [searchedActivities, selectedTag, isAdmin, ownerScope]);

  /**
   * Replica lato UI lo stesso vincolo già imposto dalla RLS UPDATE/DELETE
   * (migration 20260713110013/20260713110015): non sostituisce la sicurezza server-side,
   * serve solo a nascondere azioni che il server rifiuterebbe comunque.
   */
  const canManage = (activity: LibraryActivity): boolean => {
    if (isAdmin) return true;
    if (!user) return false;
    if (activity.owner_type === 'user') return activity.owner_id === user.id;
    return userRole === 'agency' && activity.owner_id === user.id;
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSettled: () => setDeleteTarget(null),
    });
  };

  return (
    <TooltipProvider>
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('library.tableSearchPlaceholder')}
              className="pl-9"
            />
          </div>

          {isAdmin && (
            <Select value={ownerScope} onValueChange={(value) => setOwnerScope(value as OwnerScopeFilter)}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder={t('library.ownerScopeLabel')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('library.ownerScopeAll')}</SelectItem>
                <SelectItem value="user">{t('library.ownerScopeUser')}</SelectItem>
                <SelectItem value="agency">{t('library.ownerScopeAgency')}</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {availableTags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedTag(null)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                !selectedTag
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t('library.pickerAllTags')}
            </button>
            {availableTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setSelectedTag(tag)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  selectedTag === tag
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('library.columnTitle')}</TableHead>
              <TableHead>{t('library.columnLocation')}</TableHead>
              <TableHead>{t('library.columnTags')}</TableHead>
              <TableHead>{t('library.columnOwner')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  <Loader2 className="h-5 w-5 text-gray-400 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : filteredActivities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  {t('library.pickerEmptyState')}
                </TableCell>
              </TableRow>
            ) : (
              filteredActivities.map((activity) => {
                const manageable = canManage(activity);
                return (
                  <TableRow key={activity.id}>
                    <TableCell className="font-medium">{activity.title}</TableCell>
                    <TableCell>
                      {activity.address ? (
                        <span className="flex items-center gap-1 text-sm text-gray-600">
                          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                          {activity.address}
                        </span>
                      ) : (
                        <span className="text-muted-foreground italic">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {activity.tags.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {activity.tags.map((tag) => (
                            <span
                              key={tag}
                              className="text-xs text-gray-500 bg-gray-100 rounded px-1.5 py-0.5"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground italic">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={activity.owner_type === 'agency' ? 'secondary' : 'outline'}>
                        {activity.owner_type === 'agency'
                          ? t('library.ownerAgencyBadge')
                          : t('library.ownerPersonalBadge')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" disabled>
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>{t('library.editComingSoonTooltip')}</TooltipContent>
                        </Tooltip>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                          disabled={!manageable}
                          onClick={() => setDeleteTarget(activity)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {deleteTarget && (
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('library.deleteConfirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription>{t('library.deleteConfirmDescription')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700">
                {t('common.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </TooltipProvider>
  );
}
